import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { Paper, Typography, Chip, LinearProgress, Stack, Box } from '@mui/material';

export type ApStatus = 'Offen' | 'In Arbeit' | 'Erledigt' | 'Blockiert';

export interface ApFlowNodeData extends Record<string, unknown> {
  nr: string;
  titel: string | null;
  status: ApStatus;
  progress: number;
}

export type ApFlowNodeType = Node<ApFlowNodeData, 'apFlowNode'>;

const STATUS_COLOR: Record<ApStatus, string> = {
  'Offen': '#94a3b8',
  'In Arbeit': '#0284c7',
  'Erledigt': '#16a34a',
  'Blockiert': '#dc2626',
};

const STATUS_CHIP_COLOR: Record<ApStatus, 'default' | 'info' | 'success' | 'error'> = {
  'Offen': 'default',
  'In Arbeit': 'info',
  'Erledigt': 'success',
  'Blockiert': 'error',
};

export function ApFlowNode({ data, selected }: NodeProps<ApFlowNodeType>) {
  const accent = STATUS_COLOR[data.status] ?? '#94a3b8';
  const progress = Math.max(0, Math.min(100, data.progress ?? 0));

  return (
    <Paper
      elevation={selected ? 6 : 2}
      sx={{
        width: 200,
        minHeight: 70,
        borderTop: `4px solid ${accent}`,
        borderRadius: 1,
        px: 1.25,
        py: 1,
        outline: selected ? '2px solid #1D8296' : 'none',
        outlineOffset: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#1D8296' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#1D8296' }} />

      <Stack spacing={0.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {data.nr}
          </Typography>
          <Chip
            label={data.status}
            size="small"
            color={STATUS_CHIP_COLOR[data.status] ?? 'default'}
            sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
          />
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.3,
            minHeight: 16,
          }}
        >
          {data.titel ?? '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flex: 1, height: 4, borderRadius: 1 }}
          />
          <Typography variant="caption" sx={{ minWidth: 28, fontSize: 10 }}>
            {progress}%
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
